import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, isDevMode, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, ROUTER_CONFIGURATION } from '@angular/router';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';

import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { provideAppInitializer } from '@angular/core';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { LoggerInterceptor } from '@core/apis/logger.interceptor';
import { ClinicIdInterceptor } from '../api/clinic-id.interceptor';
import { TranslocoHttpLoader } from '@core/transloco/http-loader';
import { firstValueFrom } from 'rxjs';
import { BsDatepickerModule, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { ModalModule } from 'ngx-bootstrap/modal';
import { defineLocale, getSetGlobalLocale } from 'ngx-bootstrap/chronos';
import { enGbLocale, esLocale, ukLocale } from 'ngx-bootstrap/locale';
import routes from './app.routes';

const CHUNK_RELOAD_STORAGE_KEY = 'mis.lazy-chunk-reload-at';
const CHUNK_RELOAD_COOLDOWN_MS = 10000;

function isLazyChunkLoadFailure(reason: unknown): boolean {
  const message =
    reason instanceof Error
      ? reason.message
      : typeof reason === 'string'
        ? reason
        : typeof reason === 'object' && reason !== null && 'message' in reason
          ? String(reason.message)
          : '';

  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('ChunkLoadError') ||
    message.includes('Loading chunk')
  );
}

function reloadAfterChunkFailure(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) ?? '0');

  if (Date.now() - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS) {
    return;
  }

  sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(Date.now()));
  window.location.reload();
}

export function installLazyChunkRecovery(): () => void {
  return () => {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('error', (event) => {
      if (isLazyChunkLoadFailure(event.error ?? event.message)) {
        reloadAfterChunkFailure();
      }
    });

    window.addEventListener('unhandledrejection', (event) => {
      if (isLazyChunkLoadFailure(event.reason)) {
        reloadAfterChunkFailure();
      }
    });
  };
}

class LazyChunkErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    if (isLazyChunkLoadFailure(error)) {
      reloadAfterChunkFailure();
      return;
    }

    console.error(error);
  }
}

export function preloadTranslations(): () => Promise<void> {
  return async () => {
    const transloco = inject(TranslocoService);
    await firstValueFrom(transloco.load(transloco.getActiveLang()));
  };
}

export function initBsDatepickerLocale(): () => void {
  return () => {
    defineLocale('en-gb', enGbLocale);
    defineLocale('uk', ukLocale);
    defineLocale('es', esLocale);

    const transloco = inject(TranslocoService);
    const bsLocale = inject(BsLocaleService);

    const applyLocale = (lang: string) => {
      const mapped = lang === 'ua' ? 'uk' : lang === 'en' ? 'en-gb' : lang;
      bsLocale.use(mapped);
      getSetGlobalLocale(mapped);
    };

    applyLocale(transloco.getActiveLang());
    transloco.langChanges$.subscribe(applyLocale);
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    importProvidersFrom(BsDatepickerModule.forRoot(), ModalModule.forRoot()),
    provideRouter(routes),
    {
      provide: ROUTER_CONFIGURATION,
      useValue: { paramsInheritanceStrategy: 'always' },
    },
    provideClientHydration(withEventReplay()),

    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoggerInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ClinicIdInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: LazyChunkErrorHandler,
    },

    provideTransloco({
      config: {
        availableLangs: ['en', 'ua'],
        defaultLang: 'ua',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),

    provideAppInitializer(preloadTranslations()),
    provideAppInitializer(initBsDatepickerLocale()),
    provideAppInitializer(installLazyChunkRecovery()),
  ],
};
